import { Check, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { CourseMarkdown } from "@/components/storyboard/CourseMarkdown";

interface QuizMultipleChoiceOptionProps {
  answer: string;
  isSelected: boolean;
  isAnswerCorrect: boolean;
  isSubmitted: boolean;
  isSingleChoice: boolean;
  onToggle: () => void;
  /**
   * After submit, also turn unselected correct answers green so the learner
   * can see the key. Code multiple-choice leaves those options ungraded.
   */
  revealUnselectedCorrect?: boolean;
}

/**
 * A single answer option — used by multiple-choice quizzes. Before submit,
 * only the current selection is highlighted. After submit, the chosen option
 * is graded right or wrong; unselected correct answers stay green only when
 * `revealUnselectedCorrect` is on (the default for non-code MC).
 */
const QuizMultipleChoiceOption = ({
  answer,
  isSelected,
  isAnswerCorrect,
  isSubmitted,
  isSingleChoice,
  onToggle,
  revealUnselectedCorrect = true,
}: QuizMultipleChoiceOptionProps) => {
  const chosenAnswerIsCorrect = isSubmitted && isSelected && isAnswerCorrect;
  const chosenAnswerIsWrong = isSubmitted && isSelected && !isAnswerCorrect;
  const unselectedCorrectRevealed =
    isSubmitted && !isSelected && isAnswerCorrect && revealUnselectedCorrect;

  const isCorrectState = chosenAnswerIsCorrect || unselectedCorrectRevealed;
  const isIncorrectState = chosenAnswerIsWrong;
  const isSelectedState = !isSubmitted && isSelected;
  const isNeutralState =
    !isCorrectState && !isIncorrectState && !isSelectedState;

  const buttonStyles = cn({
    "border-success bg-success-light": isCorrectState,
    "border-error bg-error-light": isIncorrectState,
    "border-secondary bg-secondary/10": isSelectedState,
    "border-gray-200 bg-gray-50": isNeutralState,
    // Hover affordance only while the option is still clickable.
    "hover:border-gray-300": isNeutralState && !isSubmitted,
  });

  const indicatorStyles = cn({
    "border-success bg-success": isCorrectState,
    "border-error bg-error": isIncorrectState,
    "border-secondary bg-secondary": isSelectedState,
    "border-gray-300 bg-white": isNeutralState,
  });

  const textStyles = cn({
    "font-semibold text-secondary": isSelectedState,
    "text-foreground": !isSelectedState,
  });

  const badgeStyles = cn({
    "bg-success/15 text-success-foreground": isAnswerCorrect,
    "bg-error/15 text-error-foreground": !isAnswerCorrect,
  });

  const indicatorContent = (() => {
    if (isCorrectState)
      return <Check className="h-4 w-4 text-success-solid-foreground" />;
    if (isIncorrectState)
      return <X className="h-4 w-4 text-error-solid-foreground" />;
    if (isSelectedState) {
      return isSingleChoice ? (
        <span className="block h-2.5 w-2.5 rounded-full bg-secondary-foreground" />
      ) : (
        <Check className="h-4 w-4 text-secondary-foreground" />
      );
    }
    return null;
  })();

  return (
    <button
      onClick={onToggle}
      disabled={isSubmitted}
      className={cn(
        "flex items-center gap-4 rounded-xl border-2 p-4 text-left transition-all duration-200",
        "disabled:cursor-not-allowed",
        buttonStyles
      )}
    >
      <div
        className={cn(
          "flex h-6 w-6 shrink-0 items-center justify-center border-2 transition-colors",
          isSingleChoice ? "rounded-full" : "rounded",
          indicatorStyles
        )}
      >
        {indicatorContent}
      </div>

      <div
        className={cn(
          "prose min-w-0 max-w-full flex-1 prose-p:my-0",
          textStyles
        )}
      >
        <CourseMarkdown>{answer}</CourseMarkdown>
      </div>

      {isSubmitted && isSelected && (
        <span
          className={cn(
            "shrink-0 whitespace-nowrap rounded-full px-2.5 py-0.5 font-semibold",
            badgeStyles
          )}
        >
          Deine Antwort
        </span>
      )}
    </button>
  );
};

export default QuizMultipleChoiceOption;
